import { Link } from "react-router-dom";
import { motion } from "../components/motion";
import { useContent } from "../context/SiteContentContext";
import { Shield, Heart, Star, Lock, ChevronRight } from "lucide-react";

const PILLAR_ICONS = [Heart, Lock, Shield, Star];

export default function AboutPage() {
  const { get } = useContent();
  const STATS = get("about.stats") || [];
  const PILLARS = (get("about.pillars") || []).map((p, i) => ({ num: String(i + 1).padStart(2, "0"), icon: PILLAR_ICONS[i % PILLAR_ICONS.length], title: p.title, desc: p.desc }));
  return (
    <div className="grain min-h-screen" style={{ background: "var(--bg-base)" }} data-testid="about-page">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden" style={{ paddingTop: "11rem", paddingBottom: "7rem" }}>
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 70% 60% at 50% 0%, rgba(201,162,75,0.08) 0%, transparent 70%)"
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 40% 40% at 80% 60%, rgba(155,27,27,0.06) 0%, transparent 70%)"
        }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <motion.div
              className="eyebrow"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              Our Story
            </motion.div>

            <div style={{ overflow: "hidden" }}>
              <motion.h1
                className="font-cormorant text-white mt-4 leading-none"
                style={{ fontSize: "clamp(3.5rem, 9vw, 7rem)" }}
                initial={{ y: "100%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                {get("about.hero_line1")}<br />
                <span style={{ color: "var(--gold)" }}>{get("about.hero_line2")}</span>
              </motion.h1>
            </div>

            <motion.div
              className="h-px mt-8 mb-8"
              style={{ background: "var(--gold)", transformOrigin: "left", maxWidth: 80 }}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            />

            <motion.p
              className="text-white/50 leading-relaxed"
              style={{ fontSize: "1.05rem", maxWidth: 560 }}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            >
              {get("about.hero_text")}
            </motion.p>
          </div>
        </div>
      </div>

      {/* ── Stats strip ── */}
      <div className="border-y border-white/[0.05]" style={{ background: "rgba(201,162,75,0.03)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="font-cormorant text-gold" style={{ fontSize: "clamp(2rem, 5vw, 3rem)" }}>
                  {s.value}
                </div>
                <div className="text-white/35 text-xs uppercase tracking-[0.2em] mt-2">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Story section ── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-28">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="eyebrow mb-5">Why we exist</div>
            <h2 className="font-cormorant text-white leading-tight mb-6" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
              Privacy first.<br />
              <span className="italic" style={{ color: "var(--gold)" }}>Always.</span>
            </h2>
            <p className="text-white/50 text-sm leading-loose mb-5">
              Couples in Indian cities have long had nowhere quiet, premium, and truly private to
              escape together. Hotels come with awkward check-ins, thin walls, and strangers in every
              corridor. We built Room XIII to fix that.
            </p>
            <p className="text-white/50 text-sm leading-loose">
              One room. Smart-lock entry. Soundproofed walls. Linen changed between every guest.
              No one on the property except you two. That is the standard we hold ourselves to —
              every single booking.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="eyebrow mb-5">How we do it</div>
            <h2 className="font-cormorant text-white leading-tight mb-6" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
              Hospitality,<br />
              <span className="italic" style={{ color: "var(--gold)" }}>reimagined.</span>
            </h2>
            <p className="text-white/50 text-sm leading-loose mb-5">
              Think of us like Airbnb — but purpose-built for couples, with a curated single
              property instead of thousands of listings. You book online, arrive at your own pace,
              and let yourself in via smart lock. No host hovering, no strangers.
            </p>
            <p className="text-white/50 text-sm leading-loose">
              Our concierge team is reachable on WhatsApp 24/7 for anything you need — from early
              check-in requests to dinner recommendations — but we stay invisible unless you call.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Pillars ── */}
      <div className="border-t border-white/[0.05]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-28">
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7 }}
          >
            <div className="eyebrow mb-4">Our values</div>
            <h2 className="font-cormorant text-white" style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}>
              What we stand for
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.num}
                className="group p-7 border border-white/[0.04] hover:border-gold/20 transition-colors duration-500"
                style={{ background: "rgba(20,17,14,0.4)" }}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Icon */}
                <div
                  className="w-10 h-10 flex items-center justify-center border border-gold/20 group-hover:border-gold/50 transition-colors duration-400 mb-6"
                >
                  <p.icon size={15} className="text-gold" />
                </div>

                {/* Ghost number */}
                <div
                  className="font-cormorant select-none mb-3"
                  style={{ fontSize: "3rem", color: "rgba(201,162,75,0.12)", lineHeight: 1 }}
                >
                  {p.num}
                </div>

                <h3 className="font-cormorant text-white text-xl mb-3">{p.title}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Promise strip ── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-16">
        <motion.div
          className="relative overflow-hidden px-10 py-16 text-center border border-gold/15"
          style={{ background: "rgba(201,162,75,0.04)" }}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7 }}
        >
          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: "radial-gradient(ellipse 60% 80% at 50% 50%, rgba(201,162,75,0.06) 0%, transparent 70%)"
          }} />

          <div className="relative">
            <div className="eyebrow mb-5">Our promise</div>
            <blockquote
              className="font-cormorant text-white italic leading-tight mb-8"
              style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
            >
              "{get("about.promise")}"
            </blockquote>
            <Link
              to="/booking"
              className="btn-gold inline-flex items-center gap-2"
              style={{ background: "linear-gradient(135deg, var(--gold) 0%, var(--gold-deep) 100%)" }}
            >
              Book Your Stay <ChevronRight size={14} />
            </Link>
          </div>
        </motion.div>
      </div>

    </div>
  );
}
